<template>
    <div>
        <TitlePrint :year="year" form-type="總額編列" />
        <SummaryData :year="year" :total-income="invoiceTotal" :total-payout="payoutTotal" />
        <div class="index-future-estimate ah-container" style="margin-top: 10px; margin-bottom: 25px">
            <div class="sub-title">{{ year }} 年度預估營業總收入</div>
            <SimpleTable
                :is-loading="isLoading"
                :chart-option="invoicePieChart"
                :table-data="invoiceData"
                :get-percentage="getPercentage"
                :total="invoiceTotal"
                @add="onAdd('invoiceData')"
                @delete="$event => onDelete($event, 'invoiceData')"
            >
            </SimpleTable>
            <SimplePrintTable :chart-option="invoicePieChart" :table-data="invoiceData" :get-percentage="getPercentage" :total="invoiceTotal" />

            <div class="sub-title">{{ year }} 年度預估營業總支出</div>
            <SimpleTable
                :is-loading="isLoading"
                :chart-option="paymentPieChart"
                :table-data="paymentData"
                :get-percentage="getPercentage"
                :total="payoutTotal"
                @add="onAdd('paymentData')"
                @delete="$event => onDelete($event, 'paymentData')"
            >
            </SimpleTable>
            <SimplePrintTable :chart-option="paymentPieChart" :table-data="paymentData" :get-percentage="getPercentage" :total="payoutTotal" />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import TitlePrint from '@/components/dashboard/Finance/Analysis/EstimatePage/TitlePrint.vue';
import SummaryData from '@/components/dashboard/Finance/Analysis/EstimatePage/SummaryData.vue';
import SimpleTable from '@/components/dashboard/Finance/Analysis/EstimatePage/SimpleTable.vue';
import SimplePrintTable from '@/components/dashboard/Finance/Analysis/EstimatePage/SimplePrintTable.vue';

export default {
    components: {
        SimpleTable,
        SimplePrintTable,
        SummaryData,
        TitlePrint,
    },
    props: {
        isLoading: Boolean,
        year: {
            type: Number,
            default: 0,
        },
        data: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            invoiceData: [],
            paymentData: [],
        };
    },
    computed: {
        // INVOICE
        invoiceTotal() {
            return _.sumBy(this.invoiceData, 'value');
        },
        invoicePieChart() {
            const option = {
                tooltip: {
                    trigger: 'item',
                    // eslint-disable-next-line max-len
                    formatter: (item) => `${item.name} ${this.getPercentage(item.value, this.invoiceTotal)} %`,
                    confine: true,
                },
                series: [
                    {
                        type: 'pie',
                        radius: ['35%', '55%'],
                        center: ['50%', '50%'],
                        avoidLabelOverlap: false,
                        label: {
                            show: false,
                        },
                        animation: false,
                        data: [],
                        emphasis: {
                            scaleSize: 10,
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 2,
                                shadowColor: 'rgba(0, 0, 0, 0.2)',
                            },
                        },
                    },
                ],
            };
            option.series[0].data = [...this.invoiceData];

            return option;
        },

        // PAYMENT
        payoutTotal() {
            return _.sumBy(this.paymentData, 'value');
        },
        paymentPieChart() {
            const option = {
                tooltip: {
                    trigger: 'item',
                    // eslint-disable-next-line max-len
                    formatter: (item) => `${item.name} ${this.getPercentage(item.value, this.payoutTotal)} %`,
                    confine: true,
                },
                series: [
                    {
                        type: 'pie',
                        radius: ['35%', '55%'],
                        center: ['50%', '50%'],
                        avoidLabelOverlap: false,
                        label: {
                            show: false,
                        },
                        animation: false,
                        data: [],
                        emphasis: {
                            scaleSize: 10,
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 2,
                                shadowColor: 'rgba(0, 0, 0, 0.2)',
                            },
                        },
                    },
                ],
            };
            option.series[0].data = [...this.paymentData];

            return option;
        },
    },
    watch: {
        data() {
            this.init();
        },
    },
    mounted() {
        this.init();
    },
    methods: {
        async init(skipAdd) {
            const resp = await this.$api({
                url: '/InvoiceManagemnetPage/GetNewestInvoceFOO_DATE',
                method: 'GET',
            });
            const { newestDate } = resp.data;

            this.invoiceData = [
                {
                    name: `實際營業總收入（截至：${moment().format('YYYY-MM-DD')}）`,
                    value: this.data.actuallyInvoiceAmount,
                },
                {
                    name: '專案未開立分期總額',
                    value: this.data.estimatedProjectAmount,
                    tooltip: '執行年度進行中專案未開立總額',
                },
                {
                    name: '非專案待開立發票總額',
                    value: this.data.estimatedInvoiceAmount,
                    tooltip: '',
                },
            ];

            if (this.data.projectedSalesRevenue > 0) {
                this.invoiceData.splice(1, 0, {
                    name: `預計銷貨收入（預開發票至：${moment(newestDate).format('YYYY-MM-DD')}）`,
                    value: this.data.projectedSalesRevenue,
                });
            }

            this.paymentData = [
                {
                    name: '實際營業總支出',
                    value: this.data.actuallyPaymentAmount,
                },
                {
                    name: '人事支出',
                    value: this.data.statisticsInfo.staffCost + this.data.statisticsInfo.salaryAmount + this.data.statisticsInfo.bonusAmount,
                },
                {
                    name: '營業支出未付款總額',
                    value: this.data.estimatedPaymentAmount,
                    tooltip: '請款單：待付款＋待審核',
                },
            ];

            if (!skipAdd) {
                // 自動產生「人事薪資預估」項目
                this.paymentData.push({ custom: true, name: '人事薪資預估', value: 0 });
            }
        },
        onAdd(addTarget) {
            this[addTarget].push({ custom: true, name: '新增項目', value: 0 });
        },
        onDelete(index, addTarget) {
            this[addTarget].splice(index, 1);
        },
        getPercentage(value, total) {
            if (!value || !total) return '--';
            return Math.round((value / total) * 10000) / 100;
        },

        getTableData() {
            const invoiceData = _.filter(this.invoiceData, ['custom', true]);
            const paymentData = _.filter(this.paymentData, ['custom', true]);

            return { invoiceData, paymentData };
        },

        async loadLocalData(localData) {
            await this.init(true);
            this.invoiceData.push(...localData.data.invoiceData);
            this.paymentData.push(...localData.data.paymentData);
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.index-future-estimate {
    .sub-title {
        color: $main-color;
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
    }

    .ah-tip .tip-icon {
        margin-bottom: -3px;
    }
}
</style>
